import 'jsoneditor-react/es/editor.min.css';

import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Col, Form, InputGroup, Modal, Row } from 'react-bootstrap';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BiSolidMinusCircle, BiSolidPlusCircle } from 'react-icons/bi';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { useAppSelector } from '@/app/hook';
import CopyVariable from '@/components/common/CopyVariable';
import { PSButton } from '@/components/ui/button';
import { selectUseId } from '@/features/auth/auth.slice';
import { BlockType } from '@/interfaces/models/Knowledge/scenario.interface';

import HintBlock from '../../../../common/HintBlock';
import PopoverWithAudio from '../../../../common/PopoverWithAudio';
import AddNoteButton from '../../AddNoteButton';
import NodeParentChanger from '../../NodeParentChanger';
import NoteForm, { getNoteSchema, INoteForm } from '../../NoteForm';
import ScenarioHeader from '../../ScenarioHeader';
import { MacBotActionKind } from '../../types';
import TextScenarioDeleteButton from './scenario-delete-button';
import ScenarioSide from './scenario-side';
import { ScenarioSimpleTextInput } from './scenario-simple-text-input';
import { TextInputWithHint } from './TextInputWithHint';

interface IFormData extends INoteForm {
  title: string;
  url: string;
  automationType?: string;
  properties?: Property[];
  responseParser?: IResponseParser[];
}

interface IResponseParser {
  oldValue?: string | null;
  newValue: string;
  key: string;
}
interface Property {
  key: string;
  value: string;
}

export const MacBotMakeWebHookForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config,
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();

  const [isDelete, setIsDelete] = useState(true);
  const [showMapGuide, setShowMapGuide] = useState(false);
  const userId = useAppSelector(selectUseId);

  const schema = Yup.object({
    title: Yup.string().trim().required(t('McBotHttpForm/scenario_http_request_title_required')),
    automationType: Yup.string().trim().optional(),
    url: Yup.string()
      .url()
      .matches(/^https:\/\//, t('McBotHttpForm/scenario_http_request_url_required'))
      .required(t('McBotHttpForm/scenario_http_request_url_required')),
    properties: Yup.array()
      .of(
        Yup.object().shape({
          key: Yup.string().trim().required(t('McBotHttpForm/webhook_request_property_key_required')),
          value: Yup.string().trim().required(t('McBotHttpForm/webhook_request_property_value_required')),
        })
      )
      .optional()
      .default([]),
    responseParser: Yup.array()
      .of(
        Yup.object().shape({
          key: Yup.string().trim().optional().nullable(),
          oldValue: Yup.string().trim().optional().nullable(),
          newValue: Yup.string().trim().required(t('McBotHttpForm/scenario_http_request_map_value_required')),
        })
      )
      .optional()
      .default([]),
    ...getNoteSchema(t),
  });

  const cleanText = (text: string) => {
    return text
      .replace(/[\n\r\t]/g, '') // Remove \n, \r, \t
      .replace(/\s+/g, ' ') // Replace multiple spaces with a single space
      .trim(); // Remove leading and trailing spaces
  };

  const {
    register,
    formState: { errors },
    reset,
    watch,
    getValues,
    setValue,
    handleSubmit,
    setError,
    clearErrors,
    control,
    trigger,
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: itemData?.title ?? '',
      url: itemData?.url ?? '',
      automationType: itemData?.automationType ?? '',
      properties: itemData?.body ?? [],
      responseParser: [],
      notes: [
        {
          note_id: uuidv4(),
          creation_date_time: new Date().toISOString(),
          title: '',
          description: '',
          qc: false,
          author: userId,
        },
      ],
    },
  });

  const {
    fields: PropertyFields,
    append: headerAppend,
    remove: headerRemove,
  } = useFieldArray({
    control,
    name: 'properties',
  });

  const {
    fields: responseParserFields,
    append: responseParserAppend,
    remove: responseParserRemove,
  } = useFieldArray({
    control,
    name: 'responseParser',
  });

  const { fields: noteFields } = useFieldArray({
    control,
    name: 'notes',
  });

  const [contextMenuList, setContextMenuList] = useState<any[]>([]);

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = (data: any) => {
    if (itemData) {
      const _data = {
        ...itemData,
        id: itemData.id,
        url: data.url,
        type: BlockType.MakeWebHook,
        parent: itemData.parent,
        automationType: data.automationType,
        body: data.properties,
        title: data.title,
        responseParser:
          data.responseParser?.map((dr: IResponseParser) => {
            return {
              newValue: dr.newValue,
              oldValue: dr.oldValue,
            };
          }) ?? [],
        notes: data.notes,
      };
      changeScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    } else {
      const _data = {
        type: BlockType.MakeWebHook,
        parent: parentId,
        title: data.title,
        url: data.url,
        automationType: data.automationType,
        body: data.properties,
        responseParser:
          data.responseParser?.map((dr: IResponseParser) => {
            return {
              newValue: dr.newValue,
              oldValue: dr.oldValue,
            };
          }) ?? [],
        notes: data.notes,
      };
      createScenarioBlock(_data).then(() => {
        reset();
        setShow(false);
      });
    }
  };

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? '',
        url: itemData?.url ?? '',
        automationType: itemData?.automationType ?? '',
        properties: itemData?.body ?? [],
        responseParser:
          itemData.responseParser?.map((dr: IResponseParser) => {
            return {
              newValue: dr.newValue,
              oldValue: dr.newValue,
              key: dr.key,
            };
          }) ?? [],
        notes: itemData?.notes,
      });
    }
  }, [itemData]);

  useEffect(() => {
    if (config.metaData.variables) {
      setContextMenuList([...config.metaData.variables]);
    }
  }, [config]);

  return (
    <React.Fragment>
      <ScenarioSide size="lg" onSubmit={handleSubmit(onSubmit)}>
        <ScenarioHeader
          type={MacBotActionKind.ShowMakeWebHookForm}
          onClose={() => {
            reset();
          }}
        />

        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Row>
            <img src="https://cdn.1cdn.app/application/LOUISEBOT/25031114181643_make_02.png" alt="discover_make" className="tw-w-full tw-h-auto" />
          </Row>
          <Row>
            <HintBlock texts={['MacMakeCom/affiliationLink']} />
            <a href="https://www.make.com/en/register?pc=louisebot" target="_blank" rel="noopener noreferrer">
              <img src="https://cdn.1cdn.app/application/LOUISEBOT/25031114181641_AD-728x90_–_1.png" alt="make_account" className="tw-w-full tw-h-auto" />
            </a>
          </Row>
          <Row>
            <TextInputWithHint
              label={t('McBotHttpForm/key4')}
              name="title"
              register={register('title')}
              error={errors.title}
              max={30}
              maxLength={30}
              HintMessage={['MacBotResponseForm/maxlenght']}
            />
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <Row className="gap-2">
            <Col xs={12}>
              <InputGroup className="tw-relative">
                <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('McBotHttpForm/key31')}
                </Form.Label>
                <ScenarioSimpleTextInput
                  className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                  placeholder="https://hook.make.com/..."
                  setValue={text => {
                    setValue('url', text?.replaceAll(/(\r\n|\n|\r)/gm, ''));

                    trigger('url').then(() => {
                      clearErrors('url');
                    });
                  }}
                  value={itemData?.url ?? getValues('url')}
                  contextMenuList={contextMenuList}
                  isInvalid={!!errors.url}
                />
                <Form.Control.Feedback type="invalid" className="d-block font-poppins">
                  {errors?.url?.message}
                </Form.Control.Feedback>
              </InputGroup>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <div className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">{t('McBotHttpForm/key27')}</div>
                </div>

                {PropertyFields.map((field, index) => (
                  <Row key={field.id} className={`tw-mt-2 tw-items-start ${index + 1 < PropertyFields.length ? 'mb-3' : ''} `}>
                    <Col md={11} xs={10}>
                      <Row className="gap-3">
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                              {t('McBotHttpForm/key29')}
                            </Form.Label>
                            <ScenarioSimpleTextInput
                              placeholder={t('McBotHttpForm/PropertyKey').toString()}
                              setValue={text => {
                                setValue(`properties.${index}.key`, text);
                              }}
                              value={getValues(`properties.${index}.key`)}
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.properties?.[index]?.key}
                            />
                            <Form.Control.Feedback type="invalid" className="tw-block">
                              {errors.properties?.[index]?.key?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col xs={12}>
                          <Form.Group className="tw-relative">
                            <Form.Label htmlFor="" className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                              {t('McBotHttpForm/key30')}
                            </Form.Label>
                            <ScenarioSimpleTextInput
                              placeholder={t('McBotHttpForm/key7').toString()}
                              setValue={text => {
                                setValue(`properties.${index}.value`, text);
                              }}
                              value={getValues(`properties.${index}.value`)}
                              contextMenuList={contextMenuList}
                              isInvalid={!!errors.properties?.[index]?.value}
                            />
                            <Form.Control.Feedback type="invalid" className="tw-block">
                              {errors.properties?.[index]?.value?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>

                    <Col md={1} xs={2} className="tw-self-stretch">
                      <button
                        id="btn-header-remove"
                        type="button"
                        className="tw-flex tw-flex-col tw-min-h-[116px] tw-h-full tw-flex-1 tw-p-2.5 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-gap-2"
                        onClick={() => headerRemove(index)}
                      >
                        <BiSolidMinusCircle className="tw-text-red-400" size={20} />
                      </button>
                    </Col>
                  </Row>
                ))}

                <PSButton
                  id="btn-make-append"
                  variant="outline"
                  type="button"
                  onClick={() => headerAppend({ key: '', value: '' })}
                  icon={<BiSolidPlusCircle className="tw-text-gray-400" />}
                  title={t('McBotHttpForm/key27')}
                />
              </div>
            </Col>
          </Row>

          <div className="h-[0px] border border-gray-200"></div>

          {/* Response mapper */}
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                  <div className="tw-flex tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                    {t('McBotHttpForm/key14')}
                    <PopoverWithAudio
                      data={{
                        contentText: t('McBotHttpForm/key21'),
                        size: 15,
                      }}
                    />
                  </div>
                </div>
                <div className="tw-flex tw-flex-col tw-gap-3">
                  <PSButton id="btn-make-map-guide" variant="outline" type="button" onClick={() => setShowMapGuide(true)} title={t('McBotHttpForm/key24')} />
                </div>

                {responseParserFields.map((field, index) => (
                  <Row key={field.id} className={`tw-mt-2 tw-items-startt tw-mr-3`}>
                    <Col md={11} xs={10}>
                      <Row className="gap-3">
                        <Col xs={12}>
                          <Form.Group>
                            <Controller
                              name={`responseParser.${index}.key`}
                              control={control}
                              render={() => <CopyVariable title={watch('title')} text={watch(`responseParser.${index}.newValue`)} noTitle />}
                            />

                            <Form.Control.Feedback type="invalid" className="tw-block">
                              {errors.responseParser?.[index]?.key?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        <Col xs={12}>
                          <Form.Group>
                            <Form.Control
                              className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                              type="text"
                              placeholder={t('McBotHttpForm/key7').toString()}
                              isInvalid={!!errors.responseParser?.[index]?.newValue}
                              {...register(`responseParser.${index}.newValue`)}
                            />
                            <Form.Control.Feedback type="invalid" className="tw-block">
                              {errors.responseParser?.[index]?.newValue?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Col>

                    <Col md={1} xs={2} className="tw-self-stretch">
                      <PSButton
                        id="btn-make-response-parser-remove"
                        variant="danger"
                        type="button"
                        onClick={() => responseParserRemove(index)}
                        icon={<BiSolidMinusCircle size={20} />}
                      />
                    </Col>
                  </Row>
                ))}

                <PSButton
                  id="btn-make-response-parser-append"
                  variant="outline"
                  type="button"
                  onClick={() => {
                    responseParserAppend({
                      newValue: '',
                      key: '',
                      oldValue: null,
                    });
                  }}
                  icon={<BiSolidPlusCircle className="tw-text-gray-400" />}
                  title={t('McBotHttpForm/key25')}
                />
              </div>
            </Col>
          </Row>
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            <TextScenarioDeleteButton itemData={itemData} tree={tree} deleteHandler={deleteHandler} />

            {itemData?.id && <NodeParentChanger tree={tree} currentId={itemData.id} />}
            <AddNoteButton noteFormMethods={{ getValues, setValue }} />
          </div>
          <div className="tw-flex tw-justify-end">
            <PSButton id="btn-make-submit" type="submit" disabled={loading} isLoading={loading} title={t('MacBotResponseForm/btn-02')} />
          </div>
        </div>

        <NoteForm noteFormMethods={{ register, watch, errors, fields: noteFields }} />
      </ScenarioSide>

      <Modal show={showMapGuide} onHide={() => setShowMapGuide(false)} size="lg" centered>
        <Modal.Body>
          <Row>
            <Col xs={12}>
              <div className="tw-flex tw-justify-between tw-items-center tw-pb-4">
                <span className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
                  {t('McBotHttpForm/key11')}
                  <PopoverWithAudio
                    data={{
                      contentText: t('McBotHttpForm/key20'),
                      size: 20,
                    }}
                  />
                </span>
              </div>

              <div className="tw-min-h-[217px] tw-justify-start tw-items-start tw-gap-2 tw-grid tw-grid-cols-12 tw-w-full">
                <div className="md:tw-col-span-4 tw-col-span-12 tw-flex tw-flex-col tw-h-full">
                  <div className="tw-overflow-auto tw-h-full tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-gray-200 tw-rounded-md tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
                    <div className="tw-self-stretch justify-between tw-items-center tw-inline-flex">
                      <div className="tw-w-[219px] tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                        {t('McBotHttpForm/key12')}
                      </div>
                      <div className="tw-w-5 tw-h-5 tw-px-[5px] tw-py-[7px] tw-opacity-0 tw-flex-col tw-justify-center tw-items-center tw-inline-flex" />
                    </div>
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                      <pre>
                        {JSON.stringify(
                          {
                            users: [{ name: 'john', email: 'john@mail.com' }],
                          },
                          null,
                          2
                        )}
                      </pre>
                    </div>
                  </div>
                </div>
                <div className="md:tw-col-span-8 tw-col-span-12 tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-justify-start items-center tw-gap-4 tw-flex">
                  <div className="tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-justify-start tw-items-start tw-gap-4 tw-flex">
                    <div className="tw-grow tw-shrink tw-basis-0 tw-h-[217px] tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
                      <div className="tw-self-stretch tw-h-[217px] tw-pl-[17px] tw-pr-[13px] tw-py-2 tw-bg-white tw-rounded-md tw-shadow border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex">
                        <div className="tw-self-stretch tw-justify-between tw-items-center tw-inline-flex">
                          <div className="tw-text-gray-500 tw-text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
                            {t('McBotHttpForm/key13')}
                            <br />
                            users.0.email
                          </div>
                          <div className="tw-w-5 tw-h-5 tw-px-[5px] tw-py-[7px] tw-opacity-0 tw-flex-col tw-justify-center tw-items-center tw-inline-flex" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};
